import React, { useState } from "react";
import Icon from "../../../icon/Icon";
import UserAvatar from "../../../user/UserAvatar";
import {
  CardTitle,
  UncontrolledDropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  Badge,
  Modal,
  ModalBody,
  Row,
  Col,
} from "reactstrap";
import { DataTableBody, DataTableHead, DataTableItem, DataTableRow } from "../../../table/DataTable";
import { Link } from "react-router-dom";

const formatOrderDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const getInitials = (name = "") => {
  const parts = name.trim().split(" ");
  return parts.length >= 2
    ? `${parts[0][0]}${parts[1][0]}`.toUpperCase()
    : (parts[0]?.[0] || "?").toUpperCase();
};

const themes = ["primary", "success", "info", "warning", "danger", "purple", "pink"];
const getTheme = (index) => themes[index % themes.length];

const TransactionTable = ({ orders = [] }) => {
  const [trans, setTrans] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const filteredOrders = orders.filter((item) => {
    if (trans === "Paid") return item.payment_status === "paid";
    if (trans === "Due")  return item.payment_status !== "paid";
    return true;
  });

  const displayOrders = filteredOrders.slice(0, 10);

  const openOrderDetail = (order) => {
    setSelectedOrder(order);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSelectedOrder(null);
  };

  const DropdownTrans = ({ item }) => (
    <UncontrolledDropdown>
      <DropdownToggle tag="a" className="text-soft dropdown-toggle btn btn-icon btn-trigger">
        <Icon name="more-h"></Icon>
      </DropdownToggle>
      <DropdownMenu end>
        <ul className="link-list-plain">
          <li>
            <DropdownItem
              tag="a"
              href="#view"
              onClick={(ev) => {
                ev.preventDefault();
                openOrderDetail(item);
              }}
            >
              <Icon name="eye" />
              <span>View Details</span>
            </DropdownItem>
          </li>
        </ul>
      </DropdownMenu>
    </UncontrolledDropdown>
  );

  return (
    <React.Fragment>
      {/* ─── Table ─────────────────────────────────────────────────── */}
      <div className="card-inner">
        <div className="card-title-group">
          <CardTitle>
            <h6 className="title">
              <span className="me-2">Recent Orders</span>{" "}
              <Link to={`${process.env.PUBLIC_URL}/orders`} className="link d-none d-sm-inline">
                See All
              </Link>
            </h6>
          </CardTitle>
          <div className="card-tools">
            <ul className="card-tools-nav">
              <li className={trans === "Paid" ? "active" : ""} onClick={() => setTrans("Paid")}>
                <a href="#paid" onClick={(ev) => ev.preventDefault()}>
                  <span>Paid</span>
                </a>
              </li>
              <li className={trans === "Due" ? "active" : ""} onClick={() => setTrans("Due")}>
                <a href="#pending" onClick={(ev) => ev.preventDefault()}>
                  <span>Pending</span>
                </a>
              </li>
              <li className={trans === "" ? "active" : ""} onClick={() => setTrans("")}>
                <a href="#all" onClick={(ev) => ev.preventDefault()}>
                  <span>All</span>
                </a>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <DataTableBody className="border-top" bodyclass="nk-tb-orders">
        <DataTableHead>
          <DataTableRow><span>Order No.</span></DataTableRow>
          <DataTableRow size="sm"><span>Customer</span></DataTableRow>
          <DataTableRow size="md"><span>Date</span></DataTableRow>
          <DataTableRow size="lg"><span>Ref</span></DataTableRow>
          <DataTableRow><span>Amount</span></DataTableRow>
          <DataTableRow><span className="d-none d-sm-inline">Status</span></DataTableRow>
          <DataTableRow><span>&nbsp;</span></DataTableRow>
        </DataTableHead>

        {displayOrders.length > 0 ? (
          displayOrders.map((item, idx) => {
            const isPaid      = item.payment_status === "paid";
            const statusColor = isPaid ? "success" : "warning";
            const statusLabel = isPaid ? "Paid" : "Due";

            return (
              <DataTableItem key={item.id || idx}>
                <DataTableRow>
                  <span className="tb-lead">
                    <a
                      href="#order"
                      onClick={(ev) => { ev.preventDefault(); openOrderDetail(item); }}
                    >
                      #{item.order_ref}
                    </a>
                  </span>
                </DataTableRow>
                <DataTableRow size="sm">
                  <div className="user-card">
                    <UserAvatar size="sm" theme={getTheme(idx)} text={getInitials(item.customer?.name)} image={null} />
                    <div className="user-name">
                      <span className="tb-lead">{item.customer?.name || "—"}</span>
                    </div>
                  </div>
                </DataTableRow>
                <DataTableRow size="md">
                  <span className="tb-sub">{formatOrderDate(item.created_at)}</span>
                </DataTableRow>
                <DataTableRow size="lg">
                  <span className="tb-sub text-primary">{item.order_ref}</span>
                </DataTableRow>
                <DataTableRow>
                  <span className="tb-sub tb-amount">
                    ₹ {Number(item.total).toLocaleString("en-IN")} <span>INR</span>
                  </span>
                </DataTableRow>
                <DataTableRow>
                  <Badge className="badge-dot badge-dot-xs" color={statusColor}>
                    {statusLabel}
                  </Badge>
                </DataTableRow>
                <DataTableRow className="nk-tb-col-action">
                  <DropdownTrans item={item} />
                </DataTableRow>
              </DataTableItem>
            );
          })
        ) : (
          <DataTableItem>
            <DataTableRow>
              <span className="text-silent">No orders found</span>
            </DataTableRow>
          </DataTableItem>
        )}
      </DataTableBody>

      {/* ─── Order Detail Modal ────────────────────────────────────── */}
      {selectedOrder && (
        <Modal isOpen={modalOpen} toggle={closeModal} className="modal-dialog-centered" size="lg">
          <ModalBody>
            <a href="#cancel" className="close" onClick={(ev) => { ev.preventDefault(); closeModal(); }}>
              <Icon name="cross-sm"></Icon>
            </a>
            <div className="nk-tnx-details mt-sm-3">
              <div className="nk-modal-head mb-3">
                <h5 className="title">Order Details</h5>
              </div>
              <Row className="gy-3">
                <Col lg={6}>
                  <span className="sub-text">Order ID</span>
                  <span className="caption-text">#{selectedOrder.order_ref}</span>
                </Col>
                <Col lg={6}>
                  <span className="sub-text">Order Status</span>
                  <Badge
                    className="badge-sm badge-dot has-bg d-none d-sm-inline-flex"
                    color={selectedOrder.order_status === "Delivered" ? "success" : "warning"}
                  >
                    {selectedOrder.order_status}
                  </Badge>
                </Col>
                <Col lg={6}>
                  <span className="sub-text">Payment Method</span>
                  <span className="caption-text">
                    {selectedOrder.payment_method === "cod" ? "Cash on Delivery" : "Online"}
                  </span>
                </Col>
                <Col lg={6}>
                  <span className="sub-text">Payment Status</span>
                  <Badge
                    className="badge-sm badge-dot has-bg d-none d-sm-inline-flex"
                    color={selectedOrder.payment_status === "paid" ? "success" : "warning"}
                  >
                    {selectedOrder.payment_status}
                  </Badge>
                </Col>
                <Col lg={6}>
                  <span className="sub-text">Order Date</span>
                  <span className="caption-text">{formatOrderDate(selectedOrder.created_at)}</span>
                </Col>
                <Col lg={6}>
                  <span className="sub-text">Total Price</span>
                  <span className="caption-text">₹ {Number(selectedOrder.total).toLocaleString("en-IN")}</span>
                </Col>

                {/* Products */}
                {selectedOrder?.products?.length > 0 && (
                  <>
                    <Col lg={12}>
                      <span className="sub-text fw-bold">Products</span>
                    </Col>
                    {selectedOrder.products.map((prod, i) => (
                      <React.Fragment key={i}>
                        <Col lg={12}>
                          <span className="text-soft">Item {i + 1}</span>
                        </Col>
                        <Col lg={4}>
                          <span className="sub-text">Name</span>
                          <span className="caption-text">{prod?.name}</span>
                        </Col>
                        <Col lg={2}>
                          <span className="sub-text">Variant</span>
                          <span className="caption-text">{prod?.variant || "—"}</span>
                        </Col>
                        <Col lg={2}>
                          <span className="sub-text">Qty</span>
                          <span className="caption-text">{prod?.qty}</span>
                        </Col>
                        <Col lg={2}>
                          <span className="sub-text">Price</span>
                          <span className="caption-text">₹ {prod?.price}</span>
                        </Col>
                        <Col lg={2}>
                          <span className="sub-text">Subtotal</span>
                          <span className="caption-text">₹ {(prod?.qty * prod?.price).toFixed(2)}</span>
                        </Col>
                      </React.Fragment>
                    ))}
                  </>
                )}

                {/* Customer */}
                <Col lg={12}>
                  <span className="sub-text fw-bold">Customer Info</span>
                </Col>
                <Col lg={6}>
                  <span className="sub-text">Name</span>
                  <span className="caption-text">{selectedOrder.customer?.name}</span>
                </Col>
                <Col lg={6}>
                  <span className="sub-text">Phone</span>
                  <span className="caption-text">{selectedOrder.customer?.phone}</span>
                </Col>
                <Col lg={6}>
                  <span className="sub-text">Email</span>
                  <span className="caption-text">{selectedOrder.customer?.email}</span>
                </Col>
                <Col lg={6}>
                  <span className="sub-text">Address</span>
                  <span className="caption-text">{selectedOrder.customer?.address}</span>
                </Col>
              </Row>
            </div>
          </ModalBody>
        </Modal>
      )}
    </React.Fragment>
  );
};
export default TransactionTable;
